import React, { useState } from 'react';
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function DrawingReconcile({
  part_id = '',
  matched_features = [],
  discrepancies = [],
  tolerance_mm = 0.025
}) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border border-slate-200 bg-white rounded-xl shadow-xs overflow-hidden my-3 text-slate-800 animate-in fade-in duration-200">
      {/* Header */}
      <div className="bg-slate-50/80 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-6 h-6 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
            <FileText className="w-3.5 h-3.5" />
          </div>
          <div>
            <h4 className="font-bold text-xs text-slate-900 tracking-tight flex items-center gap-2">
              <span>2D Print & 3D CAD Reconciliation</span>
              {part_id && (
                <span className="font-mono text-[10px] text-slate-400 font-normal">
                  {part_id}
                </span>
              )}
            </h4>
            <p className="text-[11px] text-slate-500 mt-0.5">
              OCR Dimension Extraction vs 3D STEP Geometry · GD&T Tolerance: ±{tolerance_mm}mm
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200/60 transition-colors cursor-pointer"
          title={expanded ? 'Collapse' : 'Expand'}
        >
          {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </button>
      </div>

      {expanded && (
        <div className="p-3 space-y-3">
          {discrepancies.length === 0 && matched_features.length === 0 && (
            <div className="p-4 text-center text-xs text-slate-400 italic">
              No drawing reconciliation records available.
            </div>
          )}

          {/* Discrepancies if any */}
          {discrepancies.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold text-red-600 uppercase tracking-wider flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                <span>Discrepancies Detected ({discrepancies.length})</span>
              </div>
              {discrepancies.map((disc, idx) => (
                <div
                  key={idx}
                  className="p-2.5 rounded-lg bg-amber-50/80 border border-amber-200 text-xs text-amber-950 space-y-1"
                >
                  <div className="flex items-center justify-between font-semibold">
                    <span>{disc.callout}</span>
                    <span className="font-mono text-[10px] text-amber-800 bg-amber-200/70 px-1.5 py-0.2 rounded">
                      {disc.feature_id}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-700">
                    <span className="font-semibold text-slate-900">CAD Measured:</span> {disc.cad_value}
                  </div>
                  <p className="text-[11px] text-amber-900 leading-relaxed font-normal">
                    {disc.issue}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Matched Table */}
          {matched_features.length > 0 && (
            <div className="space-y-1.5">
              <div className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                <span>Verified Drawing Dimensions ({matched_features.length})</span>
              </div>
              <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase font-bold text-left">
                      <th className="py-1.5 px-2.5">2D Print Callout</th>
                      <th className="py-1.5 px-2.5">CAD Feature</th>
                      <th className="py-1.5 px-2.5">CAD Measurement</th>
                      <th className="py-1.5 px-2.5">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 font-mono text-[11px]">
                    {matched_features.map((m, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/80">
                        <td className="py-1.5 px-2.5 font-sans font-semibold text-slate-800">
                          {m.callout}
                        </td>
                        <td className="py-1.5 px-2.5 text-blue-600 font-bold">{m.feature_id}</td>
                        <td className="py-1.5 px-2.5 text-slate-700">{m.cad_value}</td>
                        <td className="py-1.5 px-2.5">
                          <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">
                            {m.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
